"use client";
import React, { useState, useCallback, useEffect } from "react";
import "./ProductModal.css";
import closeIcon from '../../assets/closeIcon.png'
import AddToCartButton from '../AddToCartButton';
import image_not_found from '../../assets/image_not_found.png';
import { checkImageURL } from '../../utils/validations';
import { baseUrl } from "utils/config";
import { getImageSrc } from "utils/imageUtils";
import { PartnerService } from "../../services/PartnerService";

function normalizeImage(img) {
  if (typeof img === 'string') {
    return img;
  }
  if (img.image_url) return img;
  if (img.url) return { ...img, image_url: img.url };
  if (img.file_path) return { ...img, image_url: img.file_path };
  if (img.imageData) return { ...img, image_url: img.imageData };
  if (img.base64) return { ...img, image_base64: img.base64 };
  return img;
}

const ProductModal = ({ onClose, onAddToCart, imageData, brand, name, subtitle, description, price, id, image, image_data, inStock, partner_id, setShowPartnerDialog, partner_info, instructions }) => {
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [totalPrice, setTotalPrice] = useState(price.toFixed(3));
  const [images, setImages] = useState([]);
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const [loadingImages, setLoadingImages] = useState(false);

  const MAX_NOTE_LENGTH = 500;
  const validImageUrl = checkImageURL(imageData);

  useEffect(() => {
    let isMounted = true;
    async function fetchImages() {
      setLoadingImages(true);
      try {
        let imgs = [];
        if (id) {
          imgs = await PartnerService.getProductImages(id);
        }
        if (!imgs || imgs.length === 0) {
          if (typeof image_data === 'string' && image_data.trim()) {
            imgs = [{ image_url: image_data, image_base64: "", is_main: true }];
          } else if (typeof image === 'string' && image.trim()) {
            imgs = [{ image_url: image, image_base64: "", is_main: true }];
          } else if (typeof imageData === 'string' && imageData.trim()) {
            imgs = [{ image_url: imageData, image_base64: "", is_main: true }];
          }
        }
        // Normalize all images to support all field names
        const normalizedImgs = imgs.map(normalizeImage);
        if (isMounted) {
          setImages(normalizedImgs);
          setSelectedImageIdx(normalizedImgs.findIndex(img => img.is_main || img.isMain) !== -1 ? normalizedImgs.findIndex(img => img.is_main || img.isMain) : 0);
        }
      } catch (err) {
        setImages([]);
      } finally {
        setLoadingImages(false);
      }
    }
    fetchImages();
    return () => { isMounted = false; };
  }, [id, imageData, image, image_data]);

  const decreaseQuantity = useCallback(() => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
      setTotalPrice((price * (quantity - 1)).toFixed(3));
    }
  }, [quantity]);

  const increaseQuantity = useCallback(() => {
    setQuantity(quantity + 1);
    setTotalPrice((price * (quantity + 1)).toFixed(3));
  }, [quantity]);

  const handleNoteChange = (e) => {
    const value = e.target.value;
    setNote(value);

    if (value.length > MAX_NOTE_LENGTH) {
      setError(`Note cannot exceed ${MAX_NOTE_LENGTH} characters`);
    } else {
      setError("");
    }
  };
  const isAuthenticated = () => {
    return !!localStorage.getItem("userEmail"); // or whatever auth logic you use
  };

  const handleAddToCartClick = () => {
    if (quantity < 1) {
      setError("Quantity must be at least 1");
      return;
    }
    if (note.length > 500) {
      setError("Note is too long");
      return;
    }
    setError("");
    if (onAddToCart) {
      onAddToCart({ quantity, note });
    }
  };

  const handleModalClick = (e) => {
    if (e.target.className === "modal-overlay") {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleModalClick}>
      <div className="modal-container">
        {/* close button */}
        <div style={{ width: '100%', textAlign: 'right', }}>
          <button className="close-button" onClick={onClose} aria-label="Close modal">
            <img
              src={closeIcon}
              alt="Close"
              style={{ width: '24px', height: '24px' }}
            />
          </button>
        </div>

        {/* first part  */}
        <div className="firstPart-container">
          {/* image gallery */}
          <div className="product-image-gallery">
            {/* main image always on top */}
            <div className="product-image-wrapper">
              <img
                src={(() => {
                  const img = images[selectedImageIdx];
                  let src = image_not_found;
                  if (img) {
                    const imgData = img.imageData || img.image_data || img.image_base64;
                    const imageUrl = img.image_url;
                    const imgStr = typeof img === 'string' ? img : undefined;
                    src = `data:image/jpeg;base64,${imgData}`;
                    // 1. imageData as data URL
                    /* if (typeof imgData === 'string' && imgData.startsWith('data:image/')) {
                      const base64Data = imgData.split(',')[1] || '';
                      if (base64Data.length > 100) {
                        src = imgData;
                      }
                    } else if (typeof imgData === 'string' && (imgData.startsWith('http') || imgData.startsWith('/'))) {
                      src = imgData;
                    } else if (typeof imgData === 'string' && imgData.length > 100) {
                      src = `data:image/jpeg;base64,${imgData}`;
                    } else if (imgStr && imgStr.startsWith('data:image/')) {
                      const base64Data = imgStr.split(',')[1] || '';
                      if (base64Data.length > 100) {
                        src = imgStr;
                      }
                    } else if (imgStr && (imgStr.startsWith('http') || imgStr.startsWith('/'))) {
                      src = imgStr;
                    } else if (imgStr && imgStr.length > 100) {
                      src = `data:image/jpeg;base64,${imgStr}`;
                    } else if (typeof imageUrl === 'string' && (imageUrl.startsWith('http') || imageUrl.startsWith('/'))) {
                      src = imageUrl;
                    } else if (typeof imageUrl === 'string' && imageUrl.length > 100) {
                      src = `data:image/jpeg;base64,${imageUrl}`;
                    } */
                  }
                  //console.log('[ProductModal][UNIFIED] Main image src:', src, img);
                  return src;
                })()}
                alt={name}
                className="product-image"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = image_not_found;
                }}
              />
            </div>
            {/* thumbnails always directly below main image */}
            {images.length > 1 && (
              <div className="image-thumbnails image-thumbnails-large">
                {images.map((img, idx) => {
                  let src = image_not_found;
                  if (img) {
                    const imgData = img.imageData || img.image_data || img.image_base64;
                    const imageUrl = img.image_url;
                    const imgStr = typeof img === 'string' ? img : undefined;
                    if (typeof imgData === 'string' && imgData.startsWith('data:image/')) {
                      const base64Data = imgData.split(',')[1] || '';
                      if (base64Data.length > 100) {
                        src = imgData;
                      }
                    } else if (typeof imgData === 'string' && (imgData.startsWith('http') || imgData.startsWith('/'))) {
                      src = imgData;
                    } else if (typeof imgData === 'string' && imgData.length > 100) {
                      src = `data:image/jpeg;base64,${imgData}`;
                    } else if (imgStr && imgStr.startsWith('data:image/')) {
                      const base64Data = imgStr.split(',')[1] || '';
                      if (base64Data.length > 100) {
                        src = imgStr;
                      }
                    } else if (imgStr && (imgStr.startsWith('http') || imgStr.startsWith('/'))) {
                      src = imgStr;
                    } else if (imgStr && imgStr.length > 100) {
                      src = `data:image/jpeg;base64,${imgStr}`;
                    } else if (typeof imageUrl === 'string' && (imageUrl.startsWith('http') || imageUrl.startsWith('/'))) {
                      src = imageUrl;
                    } else if (typeof imageUrl === 'string' && imageUrl.length > 100) {
                      src = `data:image/jpeg;base64,${imageUrl}`;
                    }
                  }
                  //console.log('[ProductModal][UNIFIED] Thumbnail src:', src, img);
                  return (
                    <img
                      key={idx}
                      src={src || image_not_found}
                      alt={`thumbnail-${idx}`}
                      className={`thumbnail-image thumbnail-image-large${idx === selectedImageIdx ? ' selected' : ''}`}
                      onClick={() => setSelectedImageIdx(idx)}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* product details */}
          <div className="product-details">
            {/* <div className="brand-name">{brand}</div> */}
            <div className="product-name">{name}</div>
            {/* <div className="product-subtitle">{subtitle}</div> */}
            <div className="product-description">{description}</div>
            {typeof instructions === 'string' && instructions.trim() && (
              <div className="product-instructions">
                <b>Instructions:</b> {instructions}
              </div>
            )}
            <div className="price-quantity-container">
              <div className="product-price">{totalPrice} DT</div>
              <div className="quantity-selector">
                <span className="quantity-button" onClick={decreaseQuantity}>-</span>
                <span className="quantity-value">{quantity}</span>
                <span className="quantity-button" onClick={increaseQuantity}>+</span>
              </div>
            </div>
          </div>
        </div>

        {/* add note part */}
        <div className="product-content">

          <div className="note-section">
            <div className="note-title">Add a personal Note</div>
            <div className="note-disclaimer">
              The shop will try its best, but no refund will be issued if the
              request isn't fulfilled.
            </div>
            <textarea
              className={`note-textarea ${error ? "error" : ""}`}
              value={note}
              onChange={handleNoteChange}
              placeholder="Add your note here..."
              maxLength={MAX_NOTE_LENGTH}
            />
            {error && <div className="error-message">{error}</div>}
          </div>


        </div>

        {/* add to cart button */}
        <div className="add-to-cart-container"  style={{ backgroundColor: '#C37559', color: '#fff' }}>
          <AddToCartButton
            
            setShowPartnerDialog={setShowPartnerDialog}
            product={{ id, name, description, price, imageData, subtitle, inStock, partner_id, partner_info }}
            quantity={quantity}
            note={note}
            className="add-to-cart-button-Details"
            onClose={() => {              
              onClose();
            }}
            disabled={!(inStock === true || inStock === 'true') || quantity < 1 || note.length > 500}
          > 
            {inStock === true || inStock === 'true' ? 'Add To Cart' : 'Out of stock'}
          </AddToCartButton>
        </div>


      </div>
    </div >
  );
};

export default ProductModal;
